import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDownIcon, ArrowRightIcon } from 'lucide-react';
import { LuxuryFrame } from './LuxuryFrame';
import { ThreeDBackground } from './ThreeDBackground';
export function Hero() {
  return <section className="relative min-h-screen flex items-center justify-center bg-navy overflow-hidden pt-24 pb-12" data-id="element-159">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-radial-gold opacity-60 pointer-events-none" data-id="element-160"></div>
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjAwLCAxNjgsIDEwNiwgMC4wNSkiLz48L3N2Zz4=')] opacity-30 pointer-events-none" data-id="element-161"></div>

      {/* 3D Animated Background */}
      <ThreeDBackground variant="hero" data-id="element-162" />

      {/* Faint gold monogram watermark */}
      <div className="absolute bottom-20 right-10 font-serif text-[20rem] text-gold opacity-[0.03] rotate-12 select-none pointer-events-none hidden lg:block" data-id="element-163">
        OJ
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-164">
        {/* Left Content */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="flex flex-col items-start" data-id="element-165">
          {/* Monogram ornament + eyebrow */}
          <div className="flex items-center gap-4 mb-6" data-id="element-166">
            <div className="w-10 h-10 rounded-full border border-gold/40 flex items-center justify-center" data-id="element-167">
              <span className="font-serif text-gold text-sm font-semibold" data-id="element-168">
                OJ
              </span>
            </div>
            <div className="w-12 h-px bg-gold" data-id="element-169"></div>
            <span className="font-sans text-gold text-xs uppercase tracking-widest font-medium" data-id="element-170">
              Founder & Chairman of the Board
            </span>
          </div>

          {/* Display name line */}
          <p className="font-serif italic text-gold-bright text-xl tracking-widest uppercase mb-3" data-id="element-171">
            Dr. Ole T. Jensen
          </p>

          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-4" data-id="element-172">
            Founder & Chairman of the Board, <br className="hidden md:block" data-id="element-173" />
            <span className="text-white/90" data-id="element-174">Ditron Dental USA</span>
          </h1>

          <p className="font-sans text-gold/90 text-sm md:text-base uppercase tracking-widest font-medium mb-6 leading-relaxed border-l-2 border-gold/40 pl-4" data-id="element-175">
            Board Leadership · Corporate Governance · Driving Enterprise Value &
            Strategic Growth in Med Tech
          </p>

          {/* Decorative divider with diamond ornaments */}
          <div className="flex items-center gap-2 mb-8 w-full max-w-md" data-id="element-176">
            <div className="flex-1 h-px bg-gold/30" data-id="element-177"></div>
            <div className="w-1 h-1 bg-gold rotate-45" data-id="element-178"></div>
            <div className="w-1 h-1 bg-gold rotate-45" data-id="element-179"></div>
            <div className="w-1 h-1 bg-gold rotate-45" data-id="element-180"></div>
            <div className="flex-1 h-px bg-gold/30" data-id="element-181"></div>
          </div>

          <p className="font-sans text-white/70 text-lg md:text-xl max-w-xl mb-10 leading-relaxed font-light" data-id="element-182">
            Over four decades of industry-defining leadership across clinical
            practice, academia, and research, translating breakthrough science
            into scalable treatment models.
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-6 mb-12 w-full sm:w-auto" data-id="element-183">
            <a href="#experience" className="group w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-gold to-gold-bright text-navy font-sans font-semibold tracking-wide hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 shadow-lg shadow-gold/20 text-center flex items-center justify-center gap-2" data-id="element-184">
              View Experience
              <ArrowRightIcon size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-185" />
            </a>
            <a href="#contact" className="group w-full sm:w-auto px-8 py-4 bg-transparent border border-gold text-gold font-sans font-medium tracking-wide hover:bg-gold/10 transition-all duration-300 text-center flex items-center justify-center gap-2" data-id="element-186">
              Contact Ole
              <ArrowRightIcon size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-187" />
            </a>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-white/50 text-xs md:text-sm font-sans tracking-wide" data-id="element-188">
            <span data-id="element-189">Englewood, CO</span>
            <span className="w-1 h-1 rounded-full bg-gold/50" data-id="element-190"></span>
            <span data-id="element-191">500+ Connections</span>
            <span className="w-1 h-1 rounded-full bg-gold/50" data-id="element-192"></span>
            <span data-id="element-193">University of Michigan</span>
          </div>
        </motion.div>

        {/* Right Content - Portrait with floating badges */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 1,
        delay: 0.2,
        ease: 'easeOut'
      }} className="flex justify-center lg:justify-end relative" data-id="element-194">
          <div className="relative" data-id="element-195">
            <LuxuryFrame imageUrl="https://cdn.magicpatterns.com/uploads/dHUR5bSRNdCiH9y4nB8sXu/ChatGPT_Image_May_21,_2026,_04_23_16_PM.png" altText="Dr. Ole Jensen Portrait" caption="Dr. Ole T. Jensen, DDS, MS" data-id="element-196" />

            {/* Floating credential badges - desktop only */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 1.2,
            duration: 0.6
          }} className="hidden lg:block absolute -bottom-6 -left-8 bg-navy-rich border border-gold/40 px-4 py-3 backdrop-blur-sm shadow-lg" data-id="element-197">
              <p className="font-sans text-gold text-[10px] uppercase tracking-widest font-semibold" data-id="element-198">
                Endowed Chair · University of Utah
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 1.4,
            duration: 0.6
          }} className="hidden lg:block absolute -bottom-6 -right-8 bg-navy-rich border border-gold/40 px-4 py-3 backdrop-blur-sm shadow-lg" data-id="element-199">
              <p className="font-sans text-gold text-[10px] uppercase tracking-widest font-semibold" data-id="element-200">
                Board Chairman · Med Tech
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.5,
      duration: 1
    }} className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2" data-id="element-201">
        <span className="font-sans text-gold/60 text-xs uppercase tracking-widest" data-id="element-202">
          Scroll
        </span>
        <motion.div animate={{
        y: [0, 8, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-203">
          <ArrowDownIcon size={16} className="text-gold" data-id="element-204" />
        </motion.div>
      </motion.div>
    </section>;
}